/**
 * Utility to detect the current app from the URL pathname.
 * 
 * Handles both direct paths (/helpdesk/...) and workspace-prefixed paths
 * (/{workspace}/helpdesk/...) where the workspace slug comes first.
 */

const KNOWN_APP_SEGMENTS = ['dashboard', 'helpdesk', 'playbook', 'auth'] as const
export type AppSegment = typeof KNOWN_APP_SEGMENTS[number]

/**
 * Detect the current app from the URL pathname by scanning path segments
 * for a known app name.
 *
 * Examples:
 *   /helpdesk/tickets              → 'helpdesk'
 *   /customer-support/helpdesk/tickets → 'helpdesk'
 *   /playbook/documents            → 'playbook'
 *   /my-workspace/dashboard/home   → 'dashboard'
 *   /unknown/path                  → null
 */
export function detectAppFromPathname(pathname: string): AppSegment | null {
  const segments = pathname.split('/').filter(Boolean)
  for (const segment of segments) {
    if ((KNOWN_APP_SEGMENTS as readonly string[]).includes(segment)) {
      return segment as AppSegment
    }
  }
  return null
}

/**
 * Check whether the current browser URL belongs to a specific app.
 * Works in both local dev (port-based) and production (path-based) environments.
 */
export function isCurrentApp(app: AppSegment): boolean {
  if (typeof window === 'undefined') return false

  const hostname = window.location.hostname
  const port = window.location.port
  const pathname = window.location.pathname

  // Local dev — detect by port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portMap: Record<string, AppSegment> = {
      '5173': 'playbook',
      '5174': 'dashboard',
      '5175': 'helpdesk',
    }
    return portMap[port] === app
  }

  // Production — detect by path segment
  if (hostname === 'app.airclou.com') {
    return detectAppFromPathname(pathname) === app
  }

  // Legacy single-app domains
  if (hostname === 'dashboard.airclou.com') return app === 'dashboard'
  if (hostname === 'playbook.airclou.com') return app === 'playbook'

  return false
}

/**
 * Detect the current app from the browser environment.
 * Uses port mapping in local dev, pathname in production.
 * Returns null if the app cannot be determined.
 */
export function getCurrentApp(): AppSegment | null {
  if (typeof window === 'undefined') return null

  const hostname = window.location.hostname
  const port = window.location.port

  // Local dev — detect by port
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portMap: Record<string, AppSegment> = {
      '5173': 'playbook',
      '5174': 'dashboard',
      '5175': 'helpdesk',
    }
    return portMap[port] ?? null
  }

  // Production — detect by path segment
  if (hostname === 'app.airclou.com') {
    return detectAppFromPathname(window.location.pathname)
  }

  // Legacy single-app domains
  if (hostname === 'dashboard.airclou.com') return 'dashboard'
  if (hostname === 'playbook.airclou.com') return 'playbook'

  return null
}
